import { BreakpointObserver } from '@angular/cdk/layout';
import { HttpClient } from '@angular/common/http';
import { Component, Input, ViewChild } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { MatSidenav } from '@angular/material/sidenav';
import { ActivatedRoute, NavigationEnd, Router } from '@angular/router';
import { filter, map, mergeMap } from 'rxjs';
import { NotificationDialogComponent } from 'src/app/component/admin/notification-dialog/notification-dialog.component';
import { UserProfileComponent } from 'src/app/component/admin/user-profile/user-profile.component';
import { RoleAuthService } from 'src/app/shared/_helpers/roleAuth.guard';
import { AuthService } from 'src/app/shared/services/auth.service';
import { DashboardService } from 'src/app/shared/services/dashboard.service';
import { RoleManagementService } from 'src/app/shared/services/role-management.service';
import { UsersService } from 'src/app/shared/services/users.service';

export interface NavItem {
  displayName: string;
  disabled?: boolean;
  iconName: string;
  role: any;
  route?: string;
  children?: NavItem[];
}
@Component({
  selector: 'app-sidenav',
  templateUrl: './sidenav.component.html',
  styleUrls: ['./sidenav.component.scss'],
})
export class SidenavComponent {
  @ViewChild(MatSidenav)
  sidenav!: MatSidenav;
  @Input() screen: any;
  viewedPage: any;
  user: any;
  firstLetter: any;
  response: any;
  todayLeadLength: any;
  notificationDetail: any;
  subscriptionName: any;
  messageReceived: any;
  loading: boolean = false;
  fullPage: any;
  isFullScreen: boolean = false;
  isLoading: boolean = false;
  userName: any;
  currentUser: any;
  userPermissions: any;
  currentUserName: any;
  roleStatus: any;
  notificationCount: any;
  darkMode = false;
  menuItems: NavItem[] = [];

  constructor(
    private observer: BreakpointObserver,
    private router: Router,
    private dialog: MatDialog,
    private activatedRoute: ActivatedRoute,
    private _UsersService: UsersService,
    public authService: AuthService,
    private roleAuth: RoleAuthService,
    private _dashboardService: DashboardService,
    private http: HttpClient,
    private roleManagementService: RoleManagementService
  ) {
    this.router.events
      .pipe(
        filter((event: any) => event instanceof NavigationEnd),
        map(() => this.activatedRoute),
        map((route: any) => {
          while (route.firstChild) {
            route = route.firstChild;
          }
          return route;
        })
      )
      .pipe(
        filter((route: any) => route.outlet === 'primary'),
        mergeMap((route: any) => route.data)
      )
      .subscribe((event: any) => {
        this.viewedPage = event.title;
      });
  }

  ngOnInit(): void {
    this.http.get<NavItem[]>('assets/menuitems.json').subscribe((data) => {
      this.menuItems = data;
    });
    this.currentUserName = JSON.parse(
      localStorage.getItem('currentUser') || '{}'
    ).role.role_key;
    this.getCurrentUser();
    this.totalNotification();
    this.loadThemeFromStorage();
    this.fullPage = document.documentElement;
  }

  // Dark mode
  toggleTheme() {
    this.roleManagementService.checkUserRole().subscribe((isRoleActive) => {
      if (isRoleActive) {
        this.darkMode = !this.darkMode;
        localStorage.setItem('darkMode', JSON.stringify(this.darkMode));
        this.updateTheme();
      }
    });
  }

  loadThemeFromStorage() {
    this.roleManagementService.checkUserRole().subscribe((isRoleActive) => {
      if (isRoleActive) {
        const storedDarkMode = localStorage.getItem('darkMode');
        if (storedDarkMode === 'true') {
          this.darkMode = true;
        } else {
          this.darkMode = false;
        }
        this.updateTheme();
      }
    });
  }

  updateTheme() {
    this.roleManagementService.checkUserRole().subscribe((isRoleActive) => {
      if (isRoleActive) {
        if (this.darkMode) {
          document.body.classList.add('dark-mode');
        } else {
          document.body.classList.remove('dark-mode');
        }
      }
    });
  }

  ngAfterViewInit() {
    this.observer.observe(['(max-width: 960px)']).subscribe((res: any) => {
      if (res.matches) {
        this.sidenav.mode = 'over';
        this.sidenav.close();
      } else {
        this.sidenav.mode = 'side';
        this.sidenav.open();
      }
    });
  }
  // Get Current User
  getCurrentUser() {
    this.roleManagementService.checkUserRole().subscribe((isRoleActive) => {
      if (isRoleActive) {
        this.isLoading = true;
        this._UsersService.getCurrentUser().subscribe(
          (resp: any) => {
            this.authService.setUserPermissions(resp.data?.permission);
            this.authService.setIsAdmin(resp.data.role.allow_everything === 1);
            this.userPermissions = resp.data?.permission;
            this.currentUser = resp.data;
            this.roleStatus = resp.data;
            this.userName =
              resp.data.first_name
                .split(' ')
                .map((name: any) => name[0])
                .join('')
                .toUpperCase() +
              resp.data.last_name
                .split(' ')
                .map((name: any) => name[0])
                .join('')
                .toUpperCase();
            this.isLoading = false;
          },
          (error: any) => {
            if (error.status === 401) {
              localStorage.clear();
              this.router.navigateByUrl('/admin/login');
            }
            console.error('Error:', error);
            this.isLoading = false;
          }
        );
      }
    });
  }

  hasPermission(permission: string): boolean {
    return (
      this.userPermissions?.includes(permission) ||
      this.roleStatus?.role?.allow_everything === 1
    );
  }

  // profile Dialog
  openDialog(): void {
    this.roleManagementService.checkUserRole().subscribe((isRoleActive) => {
      if (isRoleActive) {
        const darkMode = localStorage.getItem('darkMode');
        const dialogRef = this.dialog.open(UserProfileComponent, {
          width: '375px',
          height: '100%',
          position: {
            right: '0',
            top: '0',
          },
          panelClass: darkMode == 'true' ? 'dark-theme' : 'light-theme',
        });
        dialogRef.afterClosed().subscribe((result: any) => {
          this.getCurrentUser();
        });
      }
    });
  }

  // Notification List Dialog
  openNotificationDialog(): void {
    this.roleManagementService.checkUserRole().subscribe((isRoleActive) => {
      if (isRoleActive) {
        const darkMode = localStorage.getItem('darkMode');
        const dialogRef = this.dialog.open(NotificationDialogComponent, {
          width: '400px',
          height: '100%',
          position: {
            right: '0',
            top: '0',
          },
          panelClass: darkMode == 'true' ? 'dark-theme' : 'light-theme',
        });
        dialogRef.afterClosed().subscribe((result) => {
          this.totalNotification();
        });
      }
    });
  }

  // Total notification Count
  totalNotification() {
    this.roleManagementService.checkUserRole().subscribe((isRoleActive) => {
      if (isRoleActive) {
        this._dashboardService.totalNotification().subscribe((resp: any) => {
          this.notificationCount = resp.data[0].total;
        });
      }
    });
  }

  // full screen preview
  openFullscreen() {
    this.roleManagementService.checkUserRole().subscribe((isRoleActive) => {
      if (isRoleActive) {
        if (this.fullPage.requestFullscreen) {
          this.fullPage.requestFullscreen();
        } else if (this.fullPage.mozRequestFullScreen) {
          /* Firefox */
          this.fullPage.mozRequestFullScreen();
        } else if (this.fullPage.webkitRequestFullscreen) {
          /* Chrome, Safari and Opera */
          this.fullPage.webkitRequestFullscreen();
        } else if (this.fullPage.msRequestFullscreen) {
          /* IE/Edge */
          this.fullPage.msRequestFullscreen();
        }
        this.isFullScreen = true;
      }
    });
  }

  // exit full screen
  exitFullscreen() {
    this.roleManagementService.checkUserRole().subscribe((isRoleActive) => {
      if (isRoleActive) {
        const doc = document as any;
        if (doc.exitFullscreen) {
          doc.exitFullscreen();
        } else if (doc.mozCancelFullScreen) {
          /* Firefox */
          doc.mozCancelFullScreen();
        } else if (doc.webkitExitFullscreen) {
          /* Chrome, Safari and Opera */
          doc.webkitExitFullscreen();
        } else if (doc.msExitFullscreen) {
          /* IE/Edge */
          doc.msExitFullscreen();
        }
        this.isFullScreen = false;
      }
    });
  }

  // Logout
  logout() {
    localStorage.clear();
    this.router.navigate(['/admin/login']);
  }
}
